import React, { useMemo, useRef, useState } from 'react';

import { CloudUploadOutlined, SearchOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import { uploadEmailMatchingZip } from 'apis/EmailMatchingAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Input from 'components/lib/Input';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { SelectAdvancedFilterOptionType } from 'types/common';

import {
  EMAIL_MATCHING_COMMUNICATION_TYPE_OPTIONS,
  EMAIL_MATCHING_SOURCE_FILTER_OPTIONS,
  EMAIL_MATCHING_STATUS_FILTER_OPTIONS,
  EMAIL_MATCHING_STATUS_FILTERS,
  EMAIL_MATCHING_STATUS_TABS,
  EmailMatchingStatusTabType,
} from './constants';
import EmailMatchingConfidenceFilter from './EmailMatchingConfidenceFilter';
import { EmailMatchingTabsMetricsType } from './types';

type Props = {
  search: string;
  statusFilters: number[];
  sourceFilters: number[];
  emailTypeFilters: number[];
  confidenceRange: number[];
  reviewerFilters: number[];
  reviewerOptions: SelectAdvancedFilterOptionType[];
  onSearchChange: (value: string) => void;
  onStatusFiltersChange: (values: number[]) => void;
  onSourceFiltersChange: (values: number[]) => void;
  onEmailTypeFiltersChange: (values: number[]) => void;
  onConfidenceRangeChange: (values: number[]) => void;
  onReviewerFiltersChange: (values: number[]) => void;
  tabMetrics: EmailMatchingTabsMetricsType;
  onUploadSuccess: () => Promise<void>;
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
`;

const ControlsInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SearchContainer = styled.div`
  width: 500px;
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StyledFilter = styled(SelectAdvancedFilter)``;

const TabsContainer = styled.div`
  display: flex;
  flex-flow: row;
`;

const Tab = styled.div`
  margin-right: 30px;
  color: var(--gray-dark);
  font-size: 14px;
  cursor: pointer;
  padding-bottom: 11px;

  &.active {
    color: black;
    border-bottom: solid 1px black;
  }
`;

const TabCount = styled(Typography.Text)`
  font-size: 10px;
  margin-left: 4px;
  color: inherit;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const EmailMatchingControls = ({
  search,
  statusFilters,
  sourceFilters,
  emailTypeFilters,
  confidenceRange,
  reviewerFilters,
  reviewerOptions,
  onSearchChange,
  onStatusFiltersChange,
  onSourceFiltersChange,
  onEmailTypeFiltersChange,
  onConfidenceRangeChange,
  onReviewerFiltersChange,
  tabMetrics,
  onUploadSuccess,
}: Props) => {
  const { showMessage } = useRootContext();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const activeTab = useMemo<EmailMatchingStatusTabType>(() => {
    if (statusFilters.length !== 1) {
      return EMAIL_MATCHING_STATUS_TABS.ALL;
    }

    const selectedStatus = statusFilters[0];

    if (selectedStatus === EMAIL_MATCHING_STATUS_FILTERS.MATCHED) {
      return EMAIL_MATCHING_STATUS_TABS.MATCHED;
    }

    if (selectedStatus === EMAIL_MATCHING_STATUS_FILTERS.NEEDS_REVIEW) {
      return EMAIL_MATCHING_STATUS_TABS.NEEDS_REVIEW;
    }

    if (selectedStatus === EMAIL_MATCHING_STATUS_FILTERS.NO_MATCH) {
      return EMAIL_MATCHING_STATUS_TABS.NO_MATCH;
    }

    if (selectedStatus === EMAIL_MATCHING_STATUS_FILTERS.SPAM) {
      return EMAIL_MATCHING_STATUS_TABS.SPAM;
    }

    return EMAIL_MATCHING_STATUS_TABS.ALL;
  }, [statusFilters]);

  const handleOpenFileDialog = () => {
    if (isUploading) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setIsUploading(true);
      const response = await uploadEmailMatchingZip(file);
      const importedCount = response?.imported_count ?? 0;
      await onUploadSuccess();
      showMessage('success', `ZIP uploaded successfully. Imported ${importedCount} records.`);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Failed to upload ZIP file.';
      showMessage('error', errorMessage);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleSelectStatus = (values: SelectAdvancedFilterOptionType[]) => {
    onStatusFiltersChange(values.map((item) => Number(item.value)));
  };

  const handleSelectReviewer = (values: SelectAdvancedFilterOptionType[]) => {
    onReviewerFiltersChange(values.map((item) => Number(item.value)));
  };

  const handleSelectSource = (values: SelectAdvancedFilterOptionType[]) => {
    onSourceFiltersChange(values.map((item) => Number(item.value)));
  };

  const handleSelectEmailType = (values: SelectAdvancedFilterOptionType[]) => {
    onEmailTypeFiltersChange(values.map((item) => Number(item.value)));
  };

  const handleSelectTab = (tab: EmailMatchingStatusTabType) => {
    if (tab === EMAIL_MATCHING_STATUS_TABS.ALL) {
      onStatusFiltersChange([]);
      return;
    }

    const statusByTab = {
      [EMAIL_MATCHING_STATUS_TABS.MATCHED]: EMAIL_MATCHING_STATUS_FILTERS.MATCHED,
      [EMAIL_MATCHING_STATUS_TABS.NEEDS_REVIEW]: EMAIL_MATCHING_STATUS_FILTERS.NEEDS_REVIEW,
      [EMAIL_MATCHING_STATUS_TABS.NO_MATCH]: EMAIL_MATCHING_STATUS_FILTERS.NO_MATCH,
      [EMAIL_MATCHING_STATUS_TABS.SPAM]: EMAIL_MATCHING_STATUS_FILTERS.SPAM,
    } as const;

    onStatusFiltersChange([statusByTab[tab as keyof typeof statusByTab]]);
  };

  return (
    <Container>
      <ControlsInfo>
        <SearchContainer>
          <Input
            data-test-id="email-matching-search-input"
            value={search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
            placeholder="Search by origin or community"
            prefix={<SearchOutlined />}
          />
        </SearchContainer>
        <FiltersRow>
          <StyledFilter
            className={classNames({ active: sourceFilters.length > 0 })}
            options={EMAIL_MATCHING_SOURCE_FILTER_OPTIONS}
            defaultValue={sourceFilters}
            onSelect={handleSelectSource}
            placeholder="Source"
            dataTestId="email-matching-filter-source"
          />
          <StyledFilter
            className={classNames({ active: statusFilters.length > 0 })}
            options={EMAIL_MATCHING_STATUS_FILTER_OPTIONS}
            defaultValue={statusFilters}
            onSelect={handleSelectStatus}
            placeholder="Status"
            dataTestId="email-matching-filter-status"
          />
          <EmailMatchingConfidenceFilter
            confidenceRange={confidenceRange}
            onConfidenceRangeChange={onConfidenceRangeChange}
          />
          {reviewerOptions.length > 0 && (
            <StyledFilter
              className={classNames({ active: reviewerFilters.length > 0 })}
              options={reviewerOptions}
              defaultValue={reviewerFilters}
              onSelect={handleSelectReviewer}
              placeholder="Reviewer"
              dataTestId="email-matching-filter-reviewer"
            />
          )}
          <StyledFilter
            className={classNames({ active: emailTypeFilters.length > 0 })}
            options={EMAIL_MATCHING_COMMUNICATION_TYPE_OPTIONS}
            defaultValue={emailTypeFilters}
            onSelect={handleSelectEmailType}
            placeholder="Email Type"
            dataTestId="email-matching-filter-email-type"
          />
        </FiltersRow>
        <TabsContainer>
          <Tab
            className={activeTab === EMAIL_MATCHING_STATUS_TABS.ALL ? 'active' : ''}
            onClick={() => handleSelectTab(EMAIL_MATCHING_STATUS_TABS.ALL)}
            data-test-id="email-matching-tab-all"
          >
            All <TabCount data-test-id="email-matching-tab-all-count">{tabMetrics.all}</TabCount>
          </Tab>
          <Tab
            className={activeTab === EMAIL_MATCHING_STATUS_TABS.MATCHED ? 'active' : ''}
            onClick={() => handleSelectTab(EMAIL_MATCHING_STATUS_TABS.MATCHED)}
            data-test-id="email-matching-tab-matched"
          >
            Matched <TabCount data-test-id="email-matching-tab-matched-count">{tabMetrics.matched}</TabCount>
          </Tab>
          <Tab
            className={activeTab === EMAIL_MATCHING_STATUS_TABS.NEEDS_REVIEW ? 'active' : ''}
            onClick={() => handleSelectTab(EMAIL_MATCHING_STATUS_TABS.NEEDS_REVIEW)}
            data-test-id="email-matching-tab-needs-review"
          >
            Needs Review
            <TabCount data-test-id="email-matching-tab-needs-review-count">{tabMetrics.needs_review}</TabCount>
          </Tab>
          <Tab
            className={activeTab === EMAIL_MATCHING_STATUS_TABS.NO_MATCH ? 'active' : ''}
            onClick={() => handleSelectTab(EMAIL_MATCHING_STATUS_TABS.NO_MATCH)}
            data-test-id="email-matching-tab-no-match"
          >
            No Match <TabCount data-test-id="email-matching-tab-no-match-count">{tabMetrics.no_match}</TabCount>
          </Tab>
          <Tab
            className={activeTab === EMAIL_MATCHING_STATUS_TABS.SPAM ? 'active' : ''}
            onClick={() => handleSelectTab(EMAIL_MATCHING_STATUS_TABS.SPAM)}
            data-test-id="email-matching-tab-spam"
          >
            Spam <TabCount data-test-id="email-matching-tab-spam-count">{tabMetrics.spam}</TabCount>
          </Tab>
        </TabsContainer>
      </ControlsInfo>
      <div>
        <HiddenFileInput ref={fileInputRef} type="file" accept=".zip,application/zip" onChange={handleFileUpload} />
        <Button
          data-test-id="email-matching-upload-zip-btn"
          type="primary"
          icon={<CloudUploadOutlined />}
          onClick={handleOpenFileDialog}
          loading={isUploading}
        >
          Upload Zip
        </Button>
      </div>
    </Container>
  );
};

export default EmailMatchingControls;
